import { paymentService } from "../services/payment";
import useApi from "./useApi";

/**
 * Custom hook to handle job related actions
 *
 * @param {object|null} job
 * @returns {{
 *   fetchJobPaymentUrl: function(string): Promise<string|null>,
 *   downloadJobPdf: function(string): Promise<void>,
 *   postJobMark: function(string): Promise<any|null>,
 *   submitMandate: function(object): Promise<*|null>,
 *   submitPayPlan: function(number, string): Promise<*|null>
 *   submitPayDate: function(string): Promise<*|null>
 * }}
 */
const useJob = (job) => {
    const { getJson, getBlob, postJson, patchJson } = useApi();

    /**
     * Posts a job mark to the server
     * @param {string} text
     * @returns {Promise<any|null>}
     */
    const postJobMark = async (text) => {
        if (job === null || job.dpid === undefined) {
            return null;
        }

        const data = JSON.stringify({
            id: job.dpid,
            text,
            specialmark: true,
            user: {
                name: "",
                personid: ""
            }
        })

        return await postJson('/online/jobmark', data);
    }

    /**
     * Fetches the payment URL for the job
     *
     * @param {string} bankId
     * @param {string} installmentAmount 
     * @returns {Promise<string|null>}
     */
    const fetchJobPaymentUrl = async (bankId, installmentAmount, phone) => {
        if (job === null) {
            return null;
        }

        let bankparam = '';

        // save job status, because it might affect the success message after payment
        window.sessionStorage.setItem('previousJobStatus', job.statuscode);

        // add bank id to query parameters if given
        if (bankId !== undefined && bankId !== '') {
            bankparam = '&bank_id=' + bankId;
        }

        let installmentparam = '';

        if (installmentAmount !== undefined && installmentAmount !== '') {
            installmentparam = '&pay_installment=' + installmentAmount;
        }

        const dpid = job.dpid ?? 'token';
        const service = paymentService.getPaymentService();

        let phoneparam = '';
        // add phone number for Worldline Vipps payments
        if (service === paymentService.PAYMENTMETHODS.WORLDLINE && bankId === 'Vipps') {
            phoneparam = `&phone=${phone}`;
        }

        const data = await getJson(`/online/registerpayment/${dpid}` +
            `?service=${service}${bankparam}${installmentparam}${phoneparam}`);

        return data?.url ?? null;
    }

    /**
     * Downloads the job PDF
     *
     * @param {string} letterType
     * @returns {Promise<void>}
     */
    const downloadJobPdf = async (letterType) => {
        if (job === null || job.dpid === undefined) {
            return;
        }

        const jobIdsToDownload = [job.dpid];
        if (Array.isArray(job.members)) {
            job.members.forEach(member => {
                if (member?.dpid) {
                    jobIdsToDownload.push(member.dpid);
                }
            });
        }

        let requestParams = '';
        if (letterType === 'invoice') {
            requestParams = '?origtype=1&lettertype=' + letterType;
        } else {
            requestParams = '?origtype=0&lettertype=' + letterType + '&jobtype=' + job.current_letter?.jobtype + '&jobstatus=' + job.current_letter?.jobstatus;
        }

        for (const id of jobIdsToDownload) {
            const data = await getBlob('/online/pdf/' + id + requestParams, 'application/pdf');
            if (data === null) {
                continue;
            }
            
            // Creates an empty 'a' element that allows manipulating the file name
            const a = document.createElement("a");
            document.body.appendChild(a);
            a.style = "display: none";

            // Creates a BLOB object
            let newBlob = new Blob([data], { type: "application/pdf" });
            // Creates a new link
            const url = window.URL.createObjectURL(newBlob);

            a.href = url;
            a.download = "invoice_" + id + '.pdf';
            a.click();

            // Opens the link
            setTimeout(function () {
                window.URL.revokeObjectURL(url);
            }, 100);
        }
    }

    /**
     * Submits a mandate for the job
     * @param {object} authorizedPerson
     * @returns {Promise<*|null>}
     */
    const submitMandate = async (authorizedPerson) => {
        if (job === null || job.dpid === undefined) {
            return;
        }

        const data = JSON.stringify({
            id: job.dpid,
            name: authorizedPerson.name,
            phone: authorizedPerson.phone,
            email: authorizedPerson.email,
            user: {
                name: "",
                personid: ""
            }
        })

        return await postJson('/online/mandate', data);
    }

    /**
     * Submits a payment plan for the job
     * @param {number} paymentCount
     * @param {string} paymentInterval
     * @returns {Promise<*|null>}
     */
    const submitPayPlan = async (paymentCount, paymentInterval) => {
        if (job === null || job.dpid === undefined) {
            return;
        }

        const data = JSON.stringify({
            id: job.dpid,
            firstpaydate: job.firstpaydate,
            count: paymentCount,
            interval: paymentInterval
        })

        return await postJson('/online/payplan', data);
    }

    const submitPayDate = async (paymentDate) => {
        if (job === null || job.dpid === undefined) {
            return;
        }

        const data = JSON.stringify({
            id: job.dpid,
            dataset: {
                paydate: paymentDate
            }
        })

        return await patchJson('/online', data);
    }

    return {
        fetchJobPaymentUrl,
        downloadJobPdf,
        postJobMark,
        submitMandate,
        submitPayPlan,
        submitPayDate
    }
}

export default useJob
